import {
  Box,
  Input,
  Text,
  Progress,
  Checkbox,
  IconButton,
  Divider,
  useColorModeValue,
  Flex,
  Spinner,
  FormControl,
  FormErrorMessage,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { AddIcon, CloseIcon, DeleteIcon } from "@chakra-ui/icons";
import VideoLogo from "@root/assets/icons/VideoLogo";
import { useTranslation } from "react-i18next";
import { formatDuration, formatFileSize } from "@root/utils";
import { TRANSCODING_STATUS } from "@root/constants";

const VideoUpload: React.FC<VideoUploadProps> = ({
  video,
  videoIndex,
  onVideoUpdate,
  onVideoRemove,
  onFileSelect,
  setChapters,
  deleteLoading,
}) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const { t } = useTranslation("common");
  const [isTitleTouched, setIsTitleTouched] = useState(false);

  const handleFileInputChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      onFileSelect(video.id, file);
    }
  };

  const handleFileClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "video/mp4";
    input.onchange = handleFileInputChange;
    input.click();
  };

  const renderVideoUploader = () => {
    if (video?.file) return null;
    return (
      <Box>
        {!video?.file && !video?.url && !video?.urlBackup && (
          <Box mb={3}>
            <Box
              border="1px solid"
              borderColor={"#1819B4"}
              borderRadius="8px"
              p={3}
              textAlign="center"
              cursor="pointer"
              _hover={{ borderColor: "purple.400" }}
              onClick={handleFileClick}
            >
              <Flex justifyContent={"center"} alignItems={"center"} gap={1}>
                <AddIcon color={"#1819B4"} boxSize={3} />
                <Text color={"#1819B4"} fontWeight="500" fontSize={"14px"}>
                  {t("videoUpload.selectVideoFile")}
                </Text>
              </Flex>

              <Text fontSize="sm" color="gray.500" mt={1}>
                {t("videoUpload.supportedFormats")}
              </Text>
            </Box>
          </Box>
        )}

        {/* Separator */}
        {!video?.file && !video?.url && (
          <Box textAlign="center" mb={3}>
            <Divider />
            <Text
              bg={bgColor}
              px={4}
              color="gray.500"
              fontSize="sm"
              transform="translateY(-50%)"
              display="inline-block"
            >
              {t("videoUpload.or")}
            </Text>
          </Box>
        )}

        {/* Video URL Input */}
        {!video?.file && !video?.urlBackup && (
          <Input
            borderRadius={"8px"}
            border={"1px solid"}
            borderColor={"#CDD6DC"}
            _hover={{ borderColor: "#CDD6DC" }}
            _focus={{ borderColor: "#CDD6DC" }}
            h={"49px"}
            placeholder={t("videoUpload.enterVideoUrl")}
            value={video?.url || ""}
            onChange={(e) => onVideoUpdate(video?.id, "url", e.target.value)}
            mb={3}
          />
        )}
      </Box>
    );
  };

  const renderUploadProgress = () => {
    if (
      video?.isUploading &&
      video?.uploadProgress &&
      video?.uploadProgress > 0
    ) {
      return (
        <Box mb={3}>
          <Progress
            value={video?.uploadProgress}
            colorScheme="teal"
            borderRadius="full"
            mb={2}
          />
          <Text fontSize="sm" color="gray.500">
            {t("videoUpload.uploading", { progress: video?.uploadProgress })}
          </Text>
        </Box>
      );
    }
    return null;
  };

  // File info card after upload
  const renderFileInfoCard = () => {
    if (video?.file || video?.urlBackup) {
      return (
        <Box
          display="flex"
          alignItems="center"
          bg="#EAF3FF"
          borderRadius="8px"
          p={3}
          mb={3}
          gap={3}
        >
          <Box>
            <VideoLogo />
          </Box>
          <Box flex="1">
            <Text fontWeight="600" fontSize="15px">
              {video?.file ? video?.file?.name : video?.title}
            </Text>
            <Text fontSize="13px" color="#6B7A90">
              {formatFileSize(video?.size || 0)}
              {`  |  ${formatDuration(video?.duration || 0)}`}
            </Text>
          </Box>
          <IconButton
            aria-label="Remove video"
            icon={<DeleteIcon />}
            size="sm"
            variant="ghost"
            color="#7DA6FF"
            onClick={() => {
              const key = video?.file ? "file" : "urlBackup";
              onVideoUpdate(video?.id, key, undefined);
            }}
          />
        </Box>
      );
    }
  };

  return (
    <Box
      bg={bgColor}
      borderRadius="8px"
      p={4}
      border="2px dashed"
      borderColor="#CDD6DC"
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Text fontSize="16px" textColor={"#2F2F34"} fontWeight="600">
          {t("videoUpload.video", { index: videoIndex + 1 })}
        </Text>
        {video?.videoId &&
          (deleteLoading ? (
            <Spinner />
          ) : (
            <IconButton
              icon={<CloseIcon />}
              aria-label="Remove video"
              size="sm"
              variant="ghost"
              onClick={() => video?.videoId && onVideoRemove(video?.videoId)}
            />
          ))}
      </Box>

      {/* Video Processing State */}
      {video?.transcodingStatus !== TRANSCODING_STATUS.COMPLETED &&
        video?.videoId && (
          <Box
            bg="#F8FAFF"
            borderRadius="8px"
            p={4}
            mb={3}
            border="1px solid"
            borderColor="#E3F2FD"
          >
            <Flex alignItems="center" gap={3}>
              <Spinner size="md" color="#1819B4" thickness="3px" speed="0.8s" />
              <Box>
                <Text fontWeight="600" fontSize="14px" color="#2F2F34">
                  {t("videoUpload.processing")}
                </Text>
                <Text fontSize="12px" color="#6B7A90" mt={1}>
                  {t("videoUpload.processingDescription")}
                </Text>
              </Box>
            </Flex>
          </Box>
        )}

      {/* Only show other UI elements when video processing is completed */}
      {(video?.transcodingStatus === TRANSCODING_STATUS.COMPLETED ||
        !video?.videoId) && (
          <>
            {/* Video Title */}
            <FormControl
              isInvalid={isTitleTouched && !video?.title?.trim()}
              mb={3}
            >
              <Input
                borderRadius={"8px"}
                border={"1px solid"}
                borderColor={"#CDD6DC"}
                _hover={{ borderColor: "#CDD6DC" }}
                _focus={{ borderColor: "#CDD6DC" }}
                h={"49px"}
                placeholder={t("videoUpload.enterVideoTitle")}
                value={video?.title}
                onChange={(e) =>
                  onVideoUpdate(video?.id, "title", e.target.value)
                }
                onBlur={() => setIsTitleTouched(true)}
              />
              <FormErrorMessage>
                {t("course.videoTitleRequired")}
              </FormErrorMessage>
            </FormControl>

            {/* Upload Progress */}
            {renderUploadProgress()}
            {renderFileInfoCard()}
            {renderVideoUploader()}
            {/* <Checkbox
            isChecked={video?.isPublic}
            onChange={(e) =>
              onVideoUpdate(video?.id, "isPublic", e.target.checked)
            }
            colorScheme="blue"
          >
            <Text fontSize="sm" color="gray.700">
              {t("videoUpload.publicAccess")}
            </Text>
          </Checkbox> */}
          </>
        )}
    </Box>
  );
};

export default VideoUpload;
